import axios from 'axios';
import React, { useState } from "react";
import Popup from 'reactjs-popup';
import { useForm } from 'react-hook-form';

import { API_DETAILS } from '../../App';
import { clickable } from "../../utils/a11y";
import { toast } from '../../utils/toast';
import { useSubmitGuard } from '../../utils/useSubmitGuard';
import { errorMessage } from '../../utils/errorMessage';
import { US_STATES } from '../../utils/usStates';
import "../../styles/Admin-ui/Admin-PopUps.css";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * "+ Add new provider" on the admin Providers page.
 *
 * Public signup only ever creates patients, so this is how a provider account
 * is made: POST /registerStaff, which the server allows for admins only. The
 * new provider starts as Pending, like one who applied used to; the Approve
 * switch in the list makes them bookable. The initial password is set here, as
 * for assistants — the provider can change it with Forgot password.
 */
const AdminAddProvider = ({ onCreated }) => {
    const [open, setOpen] = useState(false);
    const [, guard] = useSubmitGuard();
    const { register, handleSubmit, watch, reset, formState: { errors } } = useForm({
        defaultValues: { state: 'SC' }
    });

    const close = () => {
        reset();
        setOpen(false);
    };

    const onSubmit = guard(async (data) => {
        const { confirmPassword, ...fields } = data;
        const body = {
            ...fields,
            email: fields.email.trim(),
            userType: 'PROVIDER',
        };
        try {
            await axios.post(API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + "/registerStaff", body);
            toast.success("Provider account created. Switch on Approve in the list to make them bookable.");
            close();
            if (onCreated) onCreated();
        } catch (err) {
            toast.error(errorMessage(err, "We couldn't create that provider account. Check the details and try again."));
        }
    });

    const field = (name, label, rules, props = {}) => (
        <div className="myself-input-assist">
            <label htmlFor={`add-provider-${name}`}>{label}{rules.required ? ' *' : ''}</label>
            <input id={`add-provider-${name}`} className="input-field input-border" type="text" {...props} {...register(name, rules)} />
            {errors?.[name] && <p className="error-text">{errors[name].message || "This field is required"}</p>}
        </div>
    );

    return (
        <>
            <button type="button" className='btn-filled' style={{ width: 'fit-content', marginLeft: '15px' }} onClick={() => setOpen(true)}>
                + Add new provider
            </button>
            <Popup open={open} modal closeOnDocumentClick={false} onClose={close} className="congrats-popup">
                <div style={{ position: 'absolute', top: '20px', right: '20px', cursor: 'pointer' }} {...clickable(close, 'Close')}>
                    <span className="material-symbols-outlined">close</span>
                </div>
                <div style={{ padding: '50px 20px', minWidth: '30vw', maxWidth: '60vw' }}>
                    <h2 style={{ marginBottom: '20px' }}>Add a provider</h2>
                    {field("firstName", "First name", { required: true, maxLength: { value: 64, message: "Up to 64 characters" } })}
                    {field("lastName", "Last name", { required: true, maxLength: { value: 64, message: "Up to 64 characters" } })}
                    {field("email", "Email", {
                        required: true,
                        maxLength: { value: 254, message: "Up to 254 characters" },
                        pattern: { value: EMAIL_PATTERN, message: "Enter a valid email address" },
                    }, { type: "email", autoComplete: "off" })}
                    {field("phoneNumber", "Phone", {
                        required: true,
                        pattern: { value: /^\d{10}$/, message: "Enter a 10-digit phone number, digits only" },
                    }, { inputMode: "tel" })}
                    {field("license", "License number", { maxLength: { value: 64, message: "Up to 64 characters" } })}
                    {field("specialization", "Specialty", { maxLength: { value: 64, message: "Up to 64 characters" } }, { placeholder: "Urology" })}
                    <div className="myself-input-assist">
                        <label htmlFor="add-provider-state">State licensed in *</label>
                        <select id="add-provider-state" className="input-field input-border" {...register("state", { required: true })}>
                            {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                        </select>
                    </div>
                    <div className="myself-input-assist">
                        <label htmlFor="add-provider-password">Initial password *</label>
                        <input id="add-provider-password" className="input-field input-border" type="password" autoComplete="new-password"
                            {...register("password", {
                                required: true,
                                minLength: { value: 8, message: "At least 8 characters" },
                                maxLength: { value: 64, message: "Up to 64 characters" },
                            })} />
                        {errors?.password && <p className="error-text">{errors.password.message || "This field is required"}</p>}
                    </div>
                    <div className="myself-input-assist">
                        <label htmlFor="add-provider-confirm">Re-type password *</label>
                        <input id="add-provider-confirm" className="input-field input-border" type="password" autoComplete="new-password"
                            {...register("confirmPassword", { validate: (val) => watch('password') === val || "Passwords must match" })} />
                        {errors?.confirmPassword && <p className="error-text">{errors.confirmPassword.message}</p>}
                    </div>
                </div>
                <div>
                    <button type="button" className='btn-filled' style={{ width: 'fit-content', margin: '0px auto 50px auto' }} onClick={handleSubmit(onSubmit)}>
                        Create provider account
                    </button>
                </div>
            </Popup>
        </>
    );
};

export default AdminAddProvider;
